import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Globe, FileText, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button, Input, Select, Card, CardContent, Checkbox, cn } from '../../ui';
import {
  getCurso, getAreas, getConvocatorias, createCurso, updateCurso, subirImagenCurso, imagenCursoUrl,
  subirTemarioPdf, borrarTemarioPdf, temarioPdfUrl,
} from '../api';
import { getProveedores as getProveedoresLae } from '../../laeformacion/api';
import { handleAuthError } from '../../../../funciones/authHelper';
import useImagenBlob from '../../../../funciones/useImagenBlob';
import EditorRequisitos from '../components/EditorRequisitos';

const SEPE_THEME = { '--brand-dark': '21 90% 42%', '--brand-light': '25 95% 53%' };

// Límites de subida — deben coincidir con los de multer en el backend.
const MAX_PDF_BYTES = 2 * 1024 * 1024; // 2 MB (PDF de temario)
const MAX_IMG_BYTES = 5 * 1024 * 1024; // 5 MB (imagen de curso)

const ESTADOS = [
  { value: 'sin_crear', label: 'Sin crear' },
  { value: 'en_desarrollo', label: 'En desarrollo' },
  { value: 'creado', label: 'Creado' },
  { value: 'inactivo', label: 'Inactivo' },
];

const VACIO = {
  nombre: '', ref: '', ref_sepe: '', horas: '',
  convocatoria_id: '', proveedor_id: '', requisitos: '',
  // Ampliación: estado sustituye a `activo`.
  estado: 'sin_crear', temario_oficial_url: '',
  horas_minimas_pct: 85, // % sobre horas; se convierte a entero al guardar. Prefill 85.
  observaciones_internas: '', observaciones_publicas: '',
  // F2: operativos internos del proveedor.
  fecha_entrega_proveedor: '', entregado_proveedor: false,
  publicado_web: true,
};

function Bloque({ titulo, children }) {
  return (
    <Card className="mb-3"><CardContent className="p-4">
      <h2 className="text-sm font-semibold text-slate-800 mb-3">{titulo}</h2>
      {children}
    </CardContent></Card>
  );
}

export default function SepeCursoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = !!id;

  // Vuelve al listado preservando su estado (filtros, página, orden) vía back nativo.
  // Salvaguarda: si se llegó por URL directa (sin historial de app), va al listado limpio.
  const irAlListado = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/formacion/sepe/cursos');
    }
  };

  const [form, setForm] = useState(VACIO);
  const [areas, setAreas] = useState([]);
  const [convs, setConvs] = useState([]);
  const [provs, setProvs] = useState([]);
  const [selAreas, setSelAreas] = useState([]);
  const [imagen, setImagen] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [temarioPdf, setTemarioPdf] = useState(null); // F3: nombre del blob PDF
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [loading, setLoading] = useState(editando);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    getAreas().then(({ data }) => setAreas(data)).catch(() => {});
    getConvocatorias().then(({ data }) => setConvs(data)).catch(() => {});
    getProveedoresLae().then(({ data }) => setProvs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editando) return;
    setLoading(true);
    getCurso(id).then(({ data }) => {
      // % directo desde BBDD (fix pct persistente). Fallback a inversión solo
      // para cursos anteriores sin pct guardado; si no hay mínimas, vacío.
      const pct = data.horas_minimas_pct ?? (
        (data.horas_minimas != null && data.horas)
          ? Math.round((data.horas_minimas * 100) / data.horas)
          : ''
      );
      setForm({
        nombre: data.nombre || '', ref: data.ref || '', ref_sepe: data.ref_sepe || '', horas: data.horas ?? '',
        convocatoria_id: data.convocatoria_id ?? '', proveedor_id: data.proveedor_id ?? '',
        requisitos: data.requisitos || '',
        estado: data.estado || 'sin_crear',
        temario_oficial_url: data.temario_oficial_url || '',
        horas_minimas_pct: pct,
        observaciones_internas: data.observaciones_internas || '',
        observaciones_publicas: data.observaciones_publicas || '',
        fecha_entrega_proveedor: data.fecha_entrega_proveedor || '',
        entregado_proveedor: !!data.entregado_proveedor,
        publicado_web: !!data.publicado_web,
      });
      setSelAreas((data.areas || []).map((a) => a.id));
      setImagen(data.imagen || null);
      setTemarioPdf(data.temario_pdf || null);
    }).catch((e) => handleAuthError && handleAuthError(e))
      .finally(() => setLoading(false));
  }, [id, editando]);

  // Imagen ya guardada: el hook descarga el blob con Bearer y la sirve como
  // ObjectURL. La preview local de un archivo recién elegido tiene prioridad.
  const imagenUrl = useImagenBlob(imagen ? imagenCursoUrl(imagen) : null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArea = (aid) => setSelAreas((s) => (s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Validación previa: si supera el límite, aviso inmediato y NO se guarda el
    // archivo (así "Guardar" nunca intenta subir algo inválido).
    if (f.size > MAX_IMG_BYTES) {
      toast.error(`La imagen pesa ${(f.size / 1024 / 1024).toFixed(1)} MB. Máximo 5 MB. Redimensiona o comprime la imagen antes de subir.`);
      e.target.value = '';
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  // F3: subida inmediata del PDF de temario (requiere curso ya guardado).
  const onPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('El archivo debe ser un PDF.'); return; }
    // Validación previa de tamaño: aviso inmediato, no se llega a subir.
    if (f.size > MAX_PDF_BYTES) {
      toast.error(`El PDF pesa ${(f.size / 1024 / 1024).toFixed(1)} MB. Máximo 2 MB. Comprímelo con https://www.ilovepdf.com/es/comprimir_pdf antes de subir.`);
      return;
    }
    setUploadingPdf(true);
    try {
      const { data } = await subirTemarioPdf(id, f);
      setTemarioPdf(data.temario_pdf);
      toast.success('PDF de temario subido.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo subir el PDF.');
      if (handleAuthError) handleAuthError(err);
    } finally { setUploadingPdf(false); }
  };

  const onBorrarPdf = async () => {
    setUploadingPdf(true);
    try {
      await borrarTemarioPdf(id);
      setTemarioPdf(null);
      toast.success('PDF de temario eliminado.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo eliminar el PDF.');
      if (handleAuthError) handleAuthError(err);
    } finally { setUploadingPdf(false); }
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!form.convocatoria_id) { setErr('La convocatoria es obligatoria.'); return; }
    setSaving(true); setErr('');

    let cursoId = id;
    let cursoGuardado = false;
    // try/finally garantiza que `saving` SIEMPRE se resetea, aunque handleAuthError
    // relance (era la causa del botón "Guardar" girando indefinidamente).
    try {
      // 1) Guardar el curso (create/update).
      // Horas mínimas: el editor introduce %; se envían AMBOS (pct + entero
      // Math.ceil) para preservar la intención sin pérdida en el round-trip.
      const horasNum = Number(form.horas);
      const pctNum = Number(form.horas_minimas_pct);
      const tienePct = form.horas_minimas_pct !== '' && Number.isFinite(pctNum);
      const horas_minimas = (horasNum > 0 && tienePct) ? Math.ceil((horasNum * pctNum) / 100) : null;
      const payload = {
        ...form,
        horas_minimas_pct: tienePct ? pctNum : null,
        horas_minimas,
        areas: selAreas,
      };
      if (editando) await updateCurso(id, payload);
      else { const { data } = await createCurso(payload); cursoId = data.id; }
      cursoGuardado = true;

      // 2) Subir imagen si hay.
      if (file && cursoId) {
        const { data } = await subirImagenCurso(cursoId, file);
        setImagen(data.imagen);
        setFile(null);
        setPreview(null);
      }

      // Éxito: aviso UX (no bloquea) + volver al listado preservando su estado.
      if (form.estado === 'inactivo' && form.publicado_web) {
        toast.warn('El curso está publicado en web pero su estado es Inactivo. No se mostrará al público hasta que cambies el estado.', { autoClose: 6000 });
      }
      irAlListado();
    } catch (e) {
      if (!cursoGuardado) {
        setErr(e?.response?.data?.error || 'No se pudo guardar el curso.');
      } else {
        // El curso ya se guardó; solo falló la imagen. Mensaje específico para 413.
        const msg = e?.response?.status === 413
          ? (e?.response?.data?.error || 'La imagen supera el tamaño máximo permitido.')
          : (e?.response?.data?.error || e?.message || 'Imagen no subida.');
        setErr(`El curso se guardó, pero la imagen no se pudo subir: ${msg}`);
        // Si era nuevo, redirigimos al modo edición para que pueda reintentar la imagen.
        if (!editando && cursoId) navigate(`/formacion/sepe/cursos/${cursoId}`);
      }
      if (handleAuthError) handleAuthError(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bg-slate-50 px-6 py-5 min-h-full text-slate-400" style={SEPE_THEME}>Cargando…</div>;

  const imgActual = preview || imagenUrl;

  // Horas mínimas en vivo: Math.ceil(horas * % / 100). null si falta horas o %.
  const horasMinimasCalc = (Number(form.horas) > 0 && form.horas_minimas_pct !== '' && Number.isFinite(Number(form.horas_minimas_pct)))
    ? Math.ceil((Number(form.horas) * Number(form.horas_minimas_pct)) / 100)
    : null;

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full" style={SEPE_THEME}>
      <div className="mb-5 flex items-center gap-3">
        <button onClick={irAlListado} className="text-slate-400 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">{editando ? 'Editar curso' : 'Nuevo curso'}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Cursos SEPE</p>
        </div>
        <Button variant="secondary" onClick={irAlListado}>Cancelar</Button>
        <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <Bloque titulo="Datos básicos">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Ej. Excel avanzado para empresas" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ref.</label><Input value={form.ref} onChange={(e) => set('ref', e.target.value)} placeholder="LAE-1042" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ref. SEPE</label><Input value={form.ref_sepe} onChange={(e) => set('ref_sepe', e.target.value)} placeholder="ADGD0210" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Horas totales del curso</label><Input type="number" min={0} value={form.horas} onChange={(e) => set('horas', e.target.value)} /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Horas mínimas del alumno</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min={0} max={100} className="w-24"
                      value={form.horas_minimas_pct}
                      onChange={(e) => set('horas_minimas_pct', e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value))))} />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                  <span className="text-sm text-slate-400">→</span>
                  <Input type="number" readOnly tabIndex={-1} className="w-24 bg-slate-50 text-slate-600"
                    value={horasMinimasCalc === null ? '' : horasMinimasCalc} placeholder="—" />
                  <span className="text-sm text-slate-500">horas</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Porcentaje sobre las horas totales del curso. Redondeado hacia arriba.</p>
              </div>
            </div>
          </Bloque>

          <Bloque titulo="Clasificación">
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Convocatoria</label>
                <Select value={form.convocatoria_id} onChange={(e) => set('convocatoria_id', e.target.value)}>
                  <option value="">— Selecciona convocatoria —</option>
                  {convs.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Select>
                <p className="text-xs text-slate-400 mt-1">Obligatoria. Un curso pertenece a una.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Proveedor</label>
                <Select value={form.proveedor_id} onChange={(e) => set('proveedor_id', e.target.value)}>
                  <option value="">— Sin proveedor —</option>
                  {provs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </Select>
              </div>
            </div>
            {/* F2: seguimiento de entrega del proveedor (operativo interno). */}
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha límite de entrega del proveedor</label>
                <Input type="date" value={form.fecha_entrega_proveedor} onChange={(e) => set('fecha_entrega_proveedor', e.target.value)} />
                <p className="text-xs text-slate-400 mt-1">Opcional. Uso interno; no se muestra en la web.</p>
              </div>
              <div>
                {/* Etiqueta invisible: alinea el checkbox con el input de fecha (misma altura de label). */}
                <label className="block text-sm font-medium mb-1 invisible select-none" aria-hidden="true">Entregado</label>
                <label className="flex items-center gap-2.5 h-9 cursor-pointer">
                  <Checkbox checked={form.entregado_proveedor} onChange={(e) => set('entregado_proveedor', e.target.checked)} />
                  <span className="text-sm text-slate-700">El proveedor ha entregado el material</span>
                </label>
              </div>
            </div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Áreas (puede estar en varias)</label>
            <div className="flex flex-wrap gap-2">
              {areas.length === 0 && <span className="text-sm text-slate-400">No hay áreas todavía.</span>}
              {areas.map((a) => {
                const on = selAreas.includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => toggleArea(a.id)}
                    className={cn('text-sm px-3 py-1.5 rounded-full border transition',
                      on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400')}>
                    {a.nombre}
                  </button>
                );
              })}
            </div>
          </Bloque>

          <Bloque titulo="Contenido">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Temario oficial URL (interno)</label>
              <Input value={form.temario_oficial_url} onChange={(e) => set('temario_oficial_url', e.target.value)} placeholder="https://…" />
              <p className="text-xs text-slate-400 mt-1">Enlace al temario oficial para verificar contenidos internamente.</p>
            </div>

            {/* F3: PDF de temario subido (es lo que ve el alumno en la web pública) */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700 mb-1">Temario PDF</label>
              {!editando ? (
                <p className="text-xs text-slate-400">Guarda el curso primero para poder subir el PDF del temario.</p>
              ) : (
                <>
                  {temarioPdf ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <FileText className="w-4 h-4 text-rose-500" /> {temarioPdf}
                      </span>
                      <a href={temarioPdfUrl(temarioPdf)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-700 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
                        <ExternalLink className="w-4 h-4" /> Ver
                      </a>
                      <label className={cn('inline-flex items-center gap-1.5 text-sm cursor-pointer border rounded-lg px-3 py-2',
                        uploadingPdf ? 'text-slate-300 border-slate-200 cursor-not-allowed' : 'text-slate-700 border-slate-300 hover:bg-slate-50')}>
                        <ImagePlus className="w-4 h-4" /> Reemplazar
                        <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} disabled={uploadingPdf} />
                      </label>
                      <button type="button" onClick={onBorrarPdf} disabled={uploadingPdf}
                        className="inline-flex items-center gap-1.5 text-sm text-rose-600 border border-rose-200 rounded-lg px-3 py-2 hover:bg-rose-50 disabled:opacity-50">
                        <Trash2 className="w-4 h-4" /> Eliminar
                      </button>
                    </div>
                  ) : (
                    <label className={cn('inline-flex items-center gap-2 text-sm cursor-pointer border rounded-lg px-3 py-2',
                      uploadingPdf ? 'text-slate-300 border-slate-200 cursor-not-allowed' : 'text-slate-700 border-slate-300 hover:bg-slate-50')}>
                      {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                      {uploadingPdf ? 'Subiendo…' : 'Subir PDF'}
                      <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} disabled={uploadingPdf} />
                    </label>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Máximo 2 MB. Es el temario que verá el alumno en la web pública. Si tu PDF pesa más, comprímelo antes de subir en{' '}
                    <a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">ilovepdf.com/comprimir_pdf</a>.
                  </p>
                </>
              )}
            </div>
          </Bloque>

          <Bloque titulo="Requisitos">
            <p className="text-sm text-slate-500 mb-2">Texto que verán los alumnos en la web pública dentro de la sección Requisitos del curso.</p>
            <EditorRequisitos value={form.requisitos} onChange={(html) => set('requisitos', html)} />
          </Bloque>

          <Bloque titulo="Estado y observaciones">
            <div className="mb-3 md:w-1/2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
              <Select value={form.estado} onChange={(e) => set('estado', e.target.value)}>
                {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones internas</label>
              <textarea
                value={form.observaciones_internas}
                onChange={(e) => set('observaciones_internas', e.target.value.slice(0, 5000))}
                rows={3} maxLength={5000}
                placeholder="Notas internas (no se muestran al alumno)…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 resize-y"
              />
              <p className="text-xs text-slate-400 mt-0.5 text-right">{(form.observaciones_internas || '').length}/5000</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones públicas</label>
              <p className="text-sm text-slate-500 mb-2">Se muestran al alumno en la web, bajo la sección Requisitos.</p>
              <EditorRequisitos
                value={form.observaciones_publicas}
                onChange={(html) => set('observaciones_publicas', html)}
                placeholder="Escribe observaciones para el alumno..."
              />
            </div>
          </Bloque>
        </div>

        <div>
          <Bloque titulo="Imagen">
            <div className="aspect-video rounded-lg border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center mb-3">
              {imgActual ? <img src={imgActual} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-300 text-sm">Sin imagen</span>}
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
              <ImagePlus className="w-4 h-4" />
              {imgActual ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            <p className="text-xs text-slate-400 mt-2">Se sube a Azure (imagenesCursos/). Máx. 5 MB.</p>
          </Bloque>

          <Bloque titulo="Publicación">
            <label className="flex items-center gap-2.5">
              <Checkbox checked={form.publicado_web} onChange={(e) => set('publicado_web', e.target.checked)} />
              <span className="text-sm text-slate-700 flex items-center gap-1.5"><Globe className="w-4 h-4 text-emerald-600" /> Publicado en la web</span>
            </label>
            <p className="text-xs text-slate-500 mt-2">
              El estado del curso se gestiona en el bloque «Estado y observaciones». Un curso en estado <strong>Inactivo</strong> no aparece en la web aunque esté publicado.
            </p>
          </Bloque>
        </div>
      </div>

      {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
      <div className="flex justify-end gap-2 mt-3">
        <Button variant="secondary" onClick={irAlListado}>Cancelar</Button>
        <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </div>
    </div>
  );
}
